import { useLayoutEffect } from 'react';
import { FloatingPortal } from '@floating-ui/react';

import { Popover, usePopoverContext } from '../../../../store/popover/PopoverContext';
import { PopoverContent } from '../../../atoms/Popover/Popover';
import CircleLoader from '../../../atoms/CircleLoader/CircleLoader';
import { useCellHoverPreview } from '../../../../domain/tableCellHover/useCellHoverPreview';
import {
  useTableCellHoverPreviewDispatchOptional,
  useTableCellHoverPreviewStateOptional,
} from '../../../../domain/tableCellHover/useTableCellHoverPreview';
import HoverPreviewOverlay from '../HoverPreviewOverlay/HoverPreviewOverlay';
import styles from './CellHoverPreviewWrapper.module.css';

/**
 * @param {object} props
 * @param {HTMLElement} props.element
 * @returns {null}
 */
function PopoverExternalAnchor({ element }) {
  const { refs } = usePopoverContext();
  useLayoutEffect(() => {
    refs.setReference(element);
  }, [element, refs]);
  return null;
}

/**
 * Single hover-preview popover for the whole table. Anchored to the cell that
 * currently owns the hover (see CellHoverPreviewWrapper).
 * @param {object} props
 * @param {boolean} [props.isCellEditing=false]
 * @returns {JSX.Element | null}
 */
function CellHoverPreviewOverlay({ isCellEditing = false }) {
  const {
    hoverUrl, element, isPointerOnCell, cursorPosition,
  } = useTableCellHoverPreviewStateOptional();
  const { clearHoveredPreview } = useTableCellHoverPreviewDispatchOptional();

  const {
    isOpen,
    html,
    isEmpty,
    showCursorLoader,
    error,
    onPopoverMouseEnter,
    onPopoverMouseLeave,
    onOpenChange,
  } = useCellHoverPreview({
    hoverUrl,
    isPointerOnCell,
    isCellEditing,
    onClosed: clearHoveredPreview,
  });

  if (!hoverUrl && !isOpen && !showCursorLoader) {
    return null;
  }

  return (
    <>
      {showCursorLoader && cursorPosition ? (
        <FloatingPortal>
          <div
            className={styles['cursor-loader-shell']}
            style={{
              left: cursorPosition.x,
              top: cursorPosition.y,
            }}
            aria-busy="true"
            aria-live="polite"
          >
            <CircleLoader className={styles['cursor-loader']} />
          </div>
        </FloatingPortal>
      ) : null}

      {element ? (
        <Popover
          open={isOpen}
          onOpenChange={onOpenChange}
          placement="bottom-start"
          offsetX={8}
          offsetY={4}
          modal={false}
        >
          <PopoverExternalAnchor element={element} />
          <PopoverContent>
            <div
              className={styles['popover-surface']}
              onMouseEnter={onPopoverMouseEnter}
              onMouseLeave={onPopoverMouseLeave}
              aria-label="Aperçu au survol"
            >
              <HoverPreviewOverlay
                html={html}
                isEmpty={isEmpty}
                error={error}
              />
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </>
  );
}

export default CellHoverPreviewOverlay;
